import React from 'react';
import { Link } from 'react-router-dom';
import { ActivityIcon, HeartIcon, BrainIcon, EyeIcon, SparklesIcon, ThermometerIcon, CheckCircleIcon, PhoneIcon, UsersIcon, CalendarIcon, ClipboardIcon, CreditCardIcon, MailIcon, MessageSquareIcon } from 'lucide-react';
export function ClinicalServices() {
  const coreServices = [{
    icon: ActivityIcon,
    title: 'Traditional Acupuncture Therapy',
    description: 'Personalized acupuncture treatments using fine, sterile needles to stimulate specific points on the body, promoting natural healing, pain relief, and systemic balance. Each treatment is customized based on comprehensive diagnosis including tongue and pulse assessment.'
  }, {
    icon: ThermometerIcon,
    title: 'Electroacupuncture',
    description: 'Enhanced acupuncture technique that delivers gentle electrical stimulation through needles to amplify therapeutic effects, particularly effective for pain management, neurological conditions, and metabolic disorders.'
  }, {
    icon: HeartIcon,
    title: 'Integrative Medicine Consultations',
    description: 'Comprehensive health assessments combining TCM diagnostic methods (tongue diagnosis, pulse taking, constitutional assessment) with modern medical understanding to create holistic, personalized treatment plans addressing body, mind, and spirit.'
  }, {
    icon: EyeIcon,
    title: 'Tongue Diagnosis',
    description: 'Specialized diagnostic technique examining tongue color, coating, shape, and characteristics to assess internal organ function, identify imbalances, and guide treatment strategies—a cornerstone of accurate TCM diagnosis.'
  }, {
    icon: ActivityIcon,
    title: 'Pulse Diagnosis',
    description: 'Traditional assessment method evaluating pulse qualities at multiple positions to determine organ health, energy flow patterns, and disease progression, enabling precise treatment customization.'
  }, {
    icon: SparklesIcon,
    title: 'Lifestyle & Nutritional Counseling',
    description: 'Evidence-based guidance on dietary modifications, stress management techniques, sleep optimization, and lifestyle adjustments that support treatment outcomes and promote long-term wellness.'
  }, {
    icon: CheckCircleIcon,
    title: 'Preventive Health Programs',
    description: 'Proactive wellness plans emphasizing disease prevention through seasonal acupuncture treatments, constitutional balancing, immune system enhancement, and early intervention strategies.'
  }];
  const benefits = ['Personalized treatment plans tailored to your unique needs', 'Comprehensive diagnostic assessment using traditional TCM methods', 'Evidence-based approaches combining ancient wisdom with modern understanding', 'Non-invasive, drug-free treatment options', 'Holistic care addressing root causes, not just symptoms', 'Experienced, highly trained practitioners', 'Safe, sterile procedures with minimal side effects', 'Integration with conventional medical care when appropriate'];
  const patientProfiles = [{
    icon: ActivityIcon,
    title: 'Chronic Pain Management',
    description: 'Individuals with chronic musculoskeletal pain (back, neck, joint pain, arthritis) seeking nonpharmacological relief'
  }, {
    icon: BrainIcon,
    title: 'Neurological Recovery',
    description: 'Patients recovering from neurological events (stroke, paralysis, neuropathy) aiming to restore function'
  }, {
    icon: HeartIcon,
    title: 'Fertility & Reproductive Health',
    description: 'Women and men facing fertility challenges, PCOS/PCOD, menstrual disorders, or male-factor infertility'
  }, {
    icon: ThermometerIcon,
    title: 'Metabolic Conditions',
    description: 'Adults managing metabolic conditions (diabetes, thyroid imbalance, obesity, high cholesterol) through integrative approaches'
  }, {
    icon: BrainIcon,
    title: 'Stress & Mental Wellness',
    description: 'Individuals experiencing stress-related disorders, insomnia, anxiety, depression, and seeking mind-body therapies'
  }, {
    icon: SparklesIcon,
    title: 'Cosmetic Enhancement',
    description: 'Those pursuing cosmetic enhancements (facial rejuvenation, anti-aging protocols) without invasive procedures'
  }];
  const bookingSteps = [{
    number: '1',
    icon: CalendarIcon,
    title: 'Visit Our Website',
    description: 'Go to www.aiimr.in and click "Book Appointment" to get started'
  }, {
    number: '2',
    icon: ClipboardIcon,
    title: 'Select Your Service',
    description: 'Choose your service (Initial Consultation, Master Tung Therapy, etc.) and preferred practitioner'
  }, {
    number: '3',
    icon: CalendarIcon,
    title: 'Choose Date & Time',
    description: 'Select an available date and time from our real-time calendar'
  }, {
    number: '4',
    icon: CreditCardIcon,
    title: 'Complete Booking',
    description: 'Fill out the patient intake form and submit a nominal booking fee via our secure payment gateway'
  }, {
    number: '5',
    icon: MailIcon,
    title: 'Receive Confirmation',
    description: 'Get confirmation email and SMS with appointment details and pre-visit instructions'
  }];
  const testimonials = [{
    text: 'After years of chronic back pain, a 4-session Master Tung protocol gave me my life back. I can garden and play with my grandchildren again.',
    author: 'Priya S.',
    location: 'Lucknow'
  }, {
    text: "My mother regained partial mobility after stroke through Zhu Scalp Acupuncture. Her speech is clearer, and she's more independent.",
    author: 'Rakesh K.',
    location: 'Kanpur'
  }, {
    text: "Following Dr. Kapoor's fertility acupuncture program, I conceived naturally in three months. The personalized care and scientific approach made all the difference.",
    author: 'Neha M.',
    location: 'Delhi'
  }];
  return <div className="w-full bg-white" data-id="element-337">
      <section className="bg-gradient-to-br from-blue-50 to-teal-50 py-16 px-4 w-full" data-id="element-338">
        <div className="max-w-7xl mx-auto text-center" data-id="element-339">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-340">
            Clinical Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8" data-id="element-341">
            Comprehensive Acupuncture & Integrative Medicine
          </p>
          <p className="text-gray-700 max-w-4xl mx-auto leading-relaxed" data-id="element-342">
            At Aatmshakti Institute of Integrated Medical Research, we provide a
            full spectrum of Traditional Chinese Medicine and integrative
            healthcare services designed to address the root causes of health
            imbalances while treating immediate symptoms. Our patient-centered
            approach combines ancient healing wisdom with evidence-based modern
            medical understanding.
          </p>
        </div>
      </section>
      <section className="py-16 px-4 w-full" data-id="element-343">
        <div className="max-w-7xl mx-auto" data-id="element-344">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-345">
            Core Clinical Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-346">
            {coreServices.map((service, index) => <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100" data-id="element-347">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-teal-100 rounded-lg flex items-center justify-center mb-4" data-id="element-348">
                  <service.icon className="w-7 h-7 text-blue-600" data-id="element-349" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-350">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-351">
                  {service.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>
      <section className="py-16 px-4 bg-gray-50 w-full" data-id="element-352">
        <div className="max-w-7xl mx-auto" data-id="element-353">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-354">
            Typical Patient Profiles
          </h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12" data-id="element-355">
            Patients who seek care at Aatmshakti Institute of Integrated Medical
            Research and Aatmshakti Empowerment Trust include:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-356">
            {patientProfiles.map((profile, index) => <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow" data-id="element-357">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4" data-id="element-358">
                  <profile.icon className="w-6 h-6 text-blue-600" data-id="element-359" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-360">
                  {profile.title}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-361">{profile.description}</p>
              </div>)}
          </div>
        </div>
      </section>
      <section className="py-16 px-4 w-full" data-id="element-362">
        <div className="max-w-7xl mx-auto" data-id="element-363">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-364">
            How to Book Appointments
          </h2>
          <div className="grid md:grid-cols-5 gap-6 mb-12" data-id="element-365">
            {bookingSteps.map((step, index) => <div key={index} className="text-center" data-id="element-366">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-367">
                  {step.number}
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3" data-id="element-368">
                  <step.icon className="w-6 h-6 text-blue-600" data-id="element-369" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" data-id="element-370">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600" data-id="element-371">{step.description}</p>
              </div>)}
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-teal-50 p-8 rounded-2xl text-center" data-id="element-372">
            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-373">
              Need Urgent or Same-Day Appointments?
            </h3>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-374">
              <a href="tel:+918303699990" className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors" data-id="element-375">
                <PhoneIcon className="w-5 h-5 mr-2" data-id="element-376" />
                Call +91-83036 99990
              </a>
              <a href="https://wa.me/918303699990" className="inline-flex items-center justify-center bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors" data-id="element-377">
                <MessageSquareIcon className="w-5 h-5 mr-2" data-id="element-378" />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-blue-50 w-full" data-id="element-379">
        <div className="max-w-7xl mx-auto" data-id="element-380">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-381">
            Payment & Booking Information
          </h2>
          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-382">
            <div className="bg-white p-8 rounded-2xl shadow-xl" data-id="element-383">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-384">
                Secure Payment Options
              </h3>
              <p className="text-gray-700 mb-6" data-id="element-385">
                We offer multiple convenient and secure payment methods for your
                treatments and consultations:
              </p>
              <div className="space-y-4" data-id="element-386">
                <div className="flex items-center p-4 bg-blue-50 rounded-lg" data-id="element-387">
                  <CreditCardIcon className="w-6 h-6 text-blue-600 mr-3" data-id="element-388" />
                  <div data-id="element-389">
                    <h4 className="font-semibold text-gray-900" data-id="element-390">
                      Online Payment
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-391">
                      Razorpay, PayPal, Stripe - Secure card payments
                    </p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-green-50 rounded-lg" data-id="element-392">
                  <PhoneIcon className="w-6 h-6 text-green-600 mr-3" data-id="element-393" />
                  <div data-id="element-394">
                    <h4 className="font-semibold text-gray-900" data-id="element-395">
                      UPI & Digital Wallets
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-396">
                      Google Pay, PhonePe, Paytm accepted
                    </p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-purple-50 rounded-lg" data-id="element-397">
                  <CreditCardIcon className="w-6 h-6 text-purple-600 mr-3" data-id="element-398" />
                  <div data-id="element-399">
                    <h4 className="font-semibold text-gray-900" data-id="element-400">
                      In-Person Payment
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-401">
                      Cash, card, and UPI at the clinic
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg" data-id="element-402">
                <p className="text-sm text-gray-700" data-id="element-403">
                  <span className="font-semibold" data-id="element-404">Special Offers:</span> We
                  provide installment plans for course fees and group booking
                  discounts. Early-bird pricing available for workshops!
                </p>
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl" data-id="element-405">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-406">
                Flexible Appointment Booking
              </h3>
              <p className="text-gray-700 mb-6" data-id="element-407">
                Book your appointments easily through multiple channels:
              </p>
              <div className="space-y-4" data-id="element-408">
                <div className="p-4 bg-blue-50 rounded-lg" data-id="element-409">
                  <h4 className="font-semibold text-gray-900 mb-2" data-id="element-410">
                    Online Booking System
                  </h4>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-411">
                    Real-time calendar showing live availability with instant
                    confirmation
                  </p>
                  <button className="text-blue-600 font-medium hover:underline" data-id="element-412">
                    Book Online Now →
                  </button>
                </div>
                <div className="p-4 bg-green-50 rounded-lg" data-id="element-413">
                  <h4 className="font-semibold text-gray-900 mb-2" data-id="element-414">
                    WhatsApp Booking
                  </h4>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-415">
                    Message us directly for quick appointment scheduling
                  </p>
                  <a href="https://wa.me/918303699990" className="text-green-600 font-medium hover:underline" data-id="element-416">
                    WhatsApp +91 83036 99990 →
                  </a>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg" data-id="element-417">
                  <h4 className="font-semibold text-gray-900 mb-2" data-id="element-418">
                    Phone Booking
                  </h4>
                  <p className="text-sm text-gray-600 mb-3" data-id="element-419">
                    Call us for personalized assistance with scheduling
                  </p>
                  <a href="tel:+918303699990" className="text-purple-600 font-medium hover:underline" data-id="element-420">
                    Call +91 83036 99990 →
                  </a>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg" data-id="element-421">
                <p className="text-sm text-gray-700" data-id="element-422">
                  <span className="font-semibold" data-id="element-423">Automated Reminders:</span>{' '}
                  Receive confirmation and reminder notifications via email,
                  SMS, and WhatsApp before your appointment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 px-4 w-full" data-id="element-424">
        <div className="max-w-7xl mx-auto" data-id="element-425">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-426">
            <div data-id="element-427">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-428">
                Why Choose Our Clinical Services?
              </h2>
              <div className="space-y-4" data-id="element-429">
                {benefits.map((benefit, index) => <div key={index} className="flex items-start" data-id="element-430">
                    <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-431" />
                    <p className="text-gray-700" data-id="element-432">{benefit}</p>
                  </div>)}
              </div>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl" data-id="element-433">
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-434">
                Ready to Start Your Healing Journey?
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-435">
                Book a comprehensive consultation with our experienced
                practitioners to develop a personalized treatment plan tailored
                to your unique health needs and goals.
              </p>
              <Link to="/contact" data-id="element-436">
                <button className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg flex items-center justify-center" data-id="element-437">
                  <PhoneIcon className="w-5 h-5 mr-2" data-id="element-438" />
                  Book Your Consultation
                </button>
              </Link>
              <p className="text-sm text-gray-500 mt-4 text-center" data-id="element-439">
                Initial consultations include comprehensive diagnostic
                assessment
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 px-4 bg-gray-50 w-full" data-id="element-440">
        <div className="max-w-4xl mx-auto text-center" data-id="element-441">
          <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-442">
            Explore Our Specialized Treatments
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-443">
            Discover our advanced treatment modalities and specialized protocols
            for specific conditions
          </p>
          <Link to="/treatments" data-id="element-444">
            <button className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg" data-id="element-445">
              View All Treatments
            </button>
          </Link>
        </div>
      </section>
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-blue-50 w-full" data-id="element-446">
        <div className="max-w-7xl mx-auto" data-id="element-447">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-448">
            Success Stories & Testimonials
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-449">
            {testimonials.map((testimonial, index) => <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow" data-id="element-450">
                <div className="text-gray-700 italic mb-4" data-id="element-451">
                  "{testimonial.text}"
                </div>
                <div className="border-t pt-4" data-id="element-452">
                  <p className="font-semibold text-gray-900" data-id="element-453">
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-454">
                    {testimonial.location}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}